// screen: Support — one request: the conversation with Interhost support, reply, close / reopen (change support-requests)
import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { ArrowLeft, CheckCircle2, Send } from 'lucide-react';
import { ApiError, type SupportMessage } from '@viewstream/api-client';
import { Badge, Button, EmptyState, ErrorState, Label, Skeleton, cn, useToast } from '@viewstream/ui';
import { fmtDateTime } from '@viewstream/i18n';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { problemText } from '~/app/team';
import { STATUS_TONE, useCloseSupportRequest, useReplySupportRequest, useSupportLabels, useSupportRequest } from '~/app/support';
import { WhatWeSend } from '~/app/SupportContextView';

export const Route = createFileRoute('/_layout/t/$slug/support/$requestId')({ component: Screen });

const MESSAGE_MAX = 10000;

function BackLink() {
  const { t } = useLingui();
  const { tenant } = useTenant();
  return (
    <Link to={'/t/$slug/support' as never} params={{ slug: tenant.slug } as never} className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
      <ArrowLeft className="size-4 icon-dir" aria-hidden /> {t`All requests`}
    </Link>
  );
}

function Screen() {
  const { t } = useLingui();
  const { requestId } = Route.useParams();
  const { tenant } = useTenant();
  const { locale, tz } = useLocale();
  const labels = useSupportLabels();
  const toast = useToast();
  const q = useSupportRequest(tenant.slug, requestId);
  const reply = useReplySupportRequest(tenant.slug, requestId);
  const close = useCloseSupportRequest(tenant.slug, requestId);
  const [body, setBody] = React.useState('');
  const box = React.useRef<HTMLTextAreaElement>(null);
  React.useEffect(() => { document.title = appTitle(q.data?.subject || t`Support request`); }, [t, q.data?.subject]);

  if (q.isPending) return <div className="space-y-3"><Skeleton className="h-8 w-72" /><Skeleton className="h-64 w-full" /></div>;
  if (q.isError) {
    if (q.error instanceof ApiError && q.error.isNotFound) return <EmptyState title={t`Request not found`} actions={<BackLink />} />;
    return <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={{ title: t`Could not load the request`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />;
  }
  const x = q.data;
  const done = x.status === 'resolved' || x.status === 'closed';
  const when = (iso: string) => fmtDateTime(iso, { locale, tz }, { dateStyle: 'medium', timeStyle: 'short' });
  const opening: SupportMessage = { id: 'opening', request_id: x.id, author_kind: 'customer', author_email: x.contact_email, author_name: x.contact_name, body: x.message, created_at: x.created_at };

  const send = (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    reply.mutate(body.trim(), {
      onSuccess: () => { setBody(''); toast.push({ title: t`Reply sent`, tone: 'ok' }); },
      onError: (err) => toast.push({ title: err instanceof ApiError && err.status === 429 ? t`Too many messages today on this request.` : t`Could not send the reply`, description: problemText(err), tone: 'danger' }),
    });
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <BackLink />
      <header className="space-y-2">
        <h1 dir="auto" className="text-2xl font-semibold break-words">{x.subject}</h1>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Badge tone={STATUS_TONE[x.status]} data-testid="support-status">{labels.status[x.status]}</Badge>
          <Badge>{labels.category[x.category]}</Badge>
          {x.priority !== 'normal' ? <Badge tone={x.priority === 'urgent' ? 'danger' : 'warn'}>{labels.priority[x.priority]}</Badge> : null}
          <span className="text-fg-muted">{t`Opened`} <bdi>{when(x.created_at)}</bdi> · <bdi className="ltr-isolate">#{x.id.slice(-8)}</bdi></span>
        </div>
        <p className="text-xs text-fg-muted">{t`Replies also go to`} <bdi className="ltr-isolate">{x.contact_email}</bdi></p>
      </header>

      <ol className="space-y-3" aria-label={t`Conversation`}>
        {[opening, ...x.messages].map((m) => <Bubble key={m.id} m={m} when={when(m.created_at)} />)}
      </ol>

      {x.context && Object.keys(x.context).length ? <WhatWeSend context={x.context} /> : null}

      <section aria-labelledby="sr-reply" className="space-y-2 rounded-lg border border-border bg-surface p-4">
        <h2 id="sr-reply" className="font-semibold">{done ? t`Reply to reopen` : t`Reply`}</h2>
        {done ? (
          <p className="flex items-center gap-2 text-sm text-fg-muted"><CheckCircle2 className="size-4 text-ok" aria-hidden /> {x.status === 'closed' ? t`This request is closed. Write a reply to reopen it.` : t`We marked this request as resolved. Still a problem? Write a reply and it reopens.`}</p>
        ) : null}
        <form onSubmit={send} className="space-y-2">
          <Label htmlFor="sr-reply-body" className="sr-only">{t`Your reply`}</Label>
          <textarea ref={box} id="sr-reply-body" dir="auto" rows={5} maxLength={MESSAGE_MAX} value={body} onChange={(e) => setBody(e.target.value)}
            className="w-full rounded-md border border-border bg-surface p-2 text-sm" placeholder={t`Write your reply…`} />
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" loading={reply.isPending} disabled={!body.trim()}><Send className="size-4 icon-dir" aria-hidden /> {done ? t`Send and reopen` : t`Send reply`}</Button>
            {x.status !== 'closed' ? (
              <Button variant="secondary" loading={close.isPending}
                onClick={() => close.mutate(undefined, { onSuccess: () => toast.push({ title: t`Request closed`, tone: 'ok' }), onError: (err) => toast.push({ title: t`Could not close the request`, description: problemText(err), tone: 'danger' }) })}>
                {t`Close request`}
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => box.current?.focus()}>{t`Reopen`}</Button>
            )}
          </div>
        </form>
      </section>
    </div>
  );
}

function Bubble({ m, when }: { m: SupportMessage; when: string }) {
  const { t } = useLingui();
  const ours = m.author_kind === 'interhost';
  return (
    <li className={cn('rounded-lg border p-3', ours ? 'ms-6 border-primary/30 bg-primary/5' : 'me-6 border-border bg-surface')}>
      <p className="mb-1 flex flex-wrap items-center gap-x-2 text-xs text-fg-muted">
        <span className="font-medium text-fg">{ours ? t`ViewStream support` : <bdi>{m.author_name || m.author_email}</bdi>}</span>
        {ours && m.author_name ? <bdi>({m.author_name})</bdi> : null}
        <bdi>{when}</bdi>
      </p>
      <p dir="auto" className="whitespace-pre-wrap break-words text-sm">{m.body}</p>
    </li>
  );
}
